import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, TextField } from "@mui/material";
import { getLocations, getLocationsFind } from '../../../Services/Properties';
import { useTranslation } from 'react-i18next';

declare global {
  interface Window {
    google: typeof google;
  }
}

const PlacesAutocomplete: React.FunctionComponent<{ property: any, errors: any, onChangeAdress: any }> = (props) => {

  const [inputValue, setInputValue] = useState<string>([
    props.property.address?.subCommunity,
    props.property.address?.Community,
    props.property.address?.city
  ].filter(Boolean).join(", ") || "");

  const [options, setOptions] = useState<any[]>([]);
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [countryValue, setCountryValue] = useState<string>(props.property?.address?.country || "");
  const { t } = useTranslation();
  const prevCountryRef = useRef<string>();

  useEffect(() => {
    setCountryValue(props.property?.address?.country || "");
    setOptions([])
    if (prevCountryRef.current !== undefined && prevCountryRef.current !== props.property?.address?.country) {
      setInputValue("");
    }
    prevCountryRef.current = props.property?.address?.country;
  }, [props.property?.address?.country]);

  useEffect(() => {
    // Load Google Maps script dynamically
    const googleMapScript = document.createElement('script');
    googleMapScript.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyD_rakAcENsIZ3PvTOXdRmepMG89RJ7YlI&libraries=places&language=en`;
    googleMapScript.async = true;
    window.document.body.appendChild(googleMapScript);

    googleMapScript.onload = () => {
      if (window.google) {
        autocompleteServiceRef.current = new google.maps.places.AutocompleteService();
      }
    };

    return () => {
      window.document.body.removeChild(googleMapScript);
    };

  }, []);

  const handleSelectChangeLocation = (event: any, newValue: any, co: any) => {
    if (newValue) {
      setInputValue(newValue)
      setOptions([])
      getLocationsFind(newValue, co).then((data) => {
        const locationData = {
          lat: data.lat,
          lng: data.lng,
          city: data.city_en,
          community: data.community_en,
          subCommunity: data.sub_community_en,
          address: data.sub_community_en + ", " + data.community_en + ", " + data.city_en,
          country: countryValue
        };
        props.onChangeAdress(locationData);
      }).catch((error: any) => {
        console.log("error")
      })

    }
  }


  const handleSelect = (address: string | null) => {
    if (!address || !window.google) return;
    setInputValue(address);
    setError(null); // Clear previous error
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address, language: "en" }, (results: any, status) => {
      if (status === "OK" && results && results.length > 0 && results[0].geometry) {
        const addressComponents = results[0].address_components;
        const getComponent = (types: string[]) => {
          const component = addressComponents.find((comp: any) =>
            comp.types.some((type: any) => types.includes(type))
          );
          return component ? component.long_name : "";
        };
        const locationData = {
          address: address,
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng(),
          building: getComponent(["establishment", "point_of_interest", "university"]),
          subCommunity: getComponent(["route"]),
          city: getComponent(["postal_town", "locality"]),
          community: getComponent(["administrative_area_level_2"]),
          region: getComponent(["administrative_area_level_1"]),
          country: countryValue,
          postalCode: getComponent(["postal_code"])
        };
        props.onChangeAdress(locationData);
      } else {
        setError("Address not found.");
      }
    });
  };

  return (
    <>

      <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">{t("generateDescTitle.location")} *</label>

      <Autocomplete
        freeSolo
        className={`input-property mb-3 ${props.errors.address ? 'input-error' : ''}`}
        sx={{
          height: "auto !important"
        }}
        options={
          (countryValue === "UK" || countryValue === "FR")
            ? options ?? []
            : (options || []).map((location: any) => location?.LOCATION || "")
        }
        inputValue={inputValue ?? ""}
        onInputChange={(_, newValue) => {
          setInputValue(newValue || "");
          setError(null);

          if (countryValue === "UK" || countryValue === "FR") {
            if (!autocompleteServiceRef.current) return;
            autocompleteServiceRef.current.getPlacePredictions(
              {
                input: newValue || "",
                componentRestrictions: {
                  country: countryValue === "UK" ? "gb" : "fr"
                },
              } as any,
              (predictions) => {
                setOptions(predictions ? predictions.map((p) => p.description) : []);
              }
            );
          } else {
            // Call backend location search for UAE
            if (newValue) {
              getLocations(newValue, countryValue).then((data) => {
                setOptions(data || []);
              }).catch(() => setOptions([]));
            } else {
              setOptions([]);
            }
          }
        }}
        onChange={(event, newValue) => {
          if (countryValue === "UK" || countryValue === "FR") {
            handleSelect(newValue);
          } else {
            handleSelectChangeLocation(event, newValue, countryValue);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={t("generateDescTitle.cityCommunityorBuilding")}
            fullWidth
            error={!!error}
            helperText={error}
          />
        )}
      />

    </>
  );
};

export default PlacesAutocomplete;